<template>
  <div>
    <!-- 詳細表示 -->
    <template v-if="!isEditing">
      <form-item v-bind="formDataForDisplay" />
    </template>
    <template v-else>
      <component
        :is="componentName"
        v-bind="formDataForEdit"
        @change="onChange"
      />
      <!-- 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード) 別コンポーネントで対応 -->
    </template>
  </div>
</template>

<script>
import FormItem from "./FormItem";
import FormItemDatePicker from "./FormItemDatePicker";
import FormItemTimePicker from "./FormItemTimePicker";
import FormItemSpace from "./FormItemSpace";
import FormItemDateTimePicker from "./FormItemDateTimePicker";
import FormItemSelect from "./FormItemSelect";
import FormItemRadioButtons from "./FormItemRadioButtons";
import FormItemNumber from "./FormItemNumber";
import FormItemShortText from "./FormItemShortText";
import FormItemLongText from "./FormItemLongText";
import FormItemUnsupported from "./FormItemUnsupported";
import FormItemDateRangePicker from "./FormItemDateRangePicker";
import FormItemDateMultiSelect from "./FormItemDateMultiSelect";
import FormItemFlexibleTime from "./FormItemFlexibleTime";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import operationReportService from "@/services/operations/operationReportService";
import {
  calcDefaultValue,
  calcNextFormItemValue,
  calcEditComponentName,
  getLatestReportValueForFormItem,
  formatReportValueForDisplay,
} from "@/utils/operations";
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import { createNamespacedHelpers, mapGetters } from "vuex";
const { mapActions } = createNamespacedHelpers("operationReport");

const FALLBACK_VALUE = "ー";

// NOTE: 以下に対応
// 1.  年月日
// 2.  時間
// 3.  レイアウト調整用スペース
// 4.  選択項目：テキスト入力で候補絞り込みが可能であること
// 5.  ラジオボタン
// 6.  数値入力テキストエリア
// 7.  短いテキスト入力エリア
// 8.  長いテキスト入力エリア
// 9.  画像ファイルアップロード -> 別コンポーネントで対応

export default {
  name: "OperationReportShowBodyFormItem",
  components: {
    FormItem,
    FormItemDatePicker,
    FormItemTimePicker,
    FormItemSpace,
    FormItemDateTimePicker,
    FormItemSelect,
    FormItemRadioButtons,
    FormItemNumber,
    FormItemShortText,
    FormItemLongText,
    FormItemUnsupported,
    FormItemDateRangePicker,
    FormItemDateMultiSelect,
    FormItemFlexibleTime,
  },
  props: {
    // format
    enterpriseFormVersionItem: {
      type: Object,
      required: true,
    },

    reportFormVersion: {
      type: Object,
      required: true,
    },

    // 実データ
    reportFormVersionItem: {
      type: Object,
      required: true,
    },

    fallbackValue: {
      type: String,
      default: FALLBACK_VALUE,
    },

    // 編集権限
    updatable: {
      type: Boolean,
      default: false,
    },
    // 編集中かどうか
    isEditing: {
      type: Boolean,
      default: false,
    },
    // 書き込み先 ao_enterprise_vessel_report_form_version.id
    reportFormVersionId: {
      type: String,
      required: true,
    },
    // 可変行グループのセルとして使う場合の行番号。指定時は保存時に repeat_index を付与し、(form_version_item_id, repeat_index) で該当行に書き込む（通常項目では null）。
    repeatIndex: {
      type: Number,
      required: false,
      default: null,
    },
  },

  computed: {
    ...mapGetters({ masterMaps: "operationMasters/masterMaps" }),
    componentName() {
      const { enterpriseFormVersionItem } = this;
      // 項目単位 editable=false は型に関わらず表示専用（read-only フォールバック）で描画する
      if (!enterpriseFormService.isItemEditable(enterpriseFormVersionItem)) {
        return "form-item-unsupported";
      }
      const componentName = calcEditComponentName(enterpriseFormVersionItem);
      return componentName; // string
    },
    name() {
      const {
        reportFormVersion,
        reportFormVersionItem,
        enterpriseFormVersionItem,
        updatable,
      } = this;
      // 空白の場合はupdatableフラグに関係なくdisableにし、validationを通さないようにする
      const stateUpdatable =
        updatable && this.componentName !== "form-item-space"
          ? "enable"
          : "disable";
      // 可変行セルでは同一項目定義が行数分現れるため、repeat_index で validation 名を一意化する
      const repeatSuffix =
        this.repeatIndex == null ? "" : `_r${this.repeatIndex}`;
      return `operation-report-form-${stateUpdatable}_${reportFormVersion.id}_${reportFormVersionItem.id}_${enterpriseFormVersionItem.id}${repeatSuffix}`; // validationに使用
    },
    // 詳細表示用データ
    formDataForDisplay() {
      const {
        enterpriseFormVersionItem,
        reportFormVersionItemValueRead: value,
        name,
        nullableLabel,
        isNullableChecked,
        isEdited,
        fallbackValue,
        hasReportFormVersionItemValue,
        unit,
        isEditing,
        disabled,
        selectOptions,
      } = this;
      const label = enterpriseFormService.getLabelFromEnterpriseFormVersionItem(
        enterpriseFormVersionItem
      );
      const required = enterpriseFormService.getRequiredFromEnterpriseFormVersionItem(
        enterpriseFormVersionItem
      );
      return {
        label,
        required,
        name,
        value,
        unit,
        nullableLabel,
        isNullableChecked,
        isEdited,
        fallbackValue,
        hasReportFormVersionItemValue,
        isEditing,
        disabled,
        selectOptions,
      };
    },

    disabled() {
      return !this.updatable;
    },

    defaultValue() {
      const { enterpriseFormVersionItem, isEdited, disabled, isEditing } = this;
      if (disabled) return null; // 非活性ならデフォルト値を使用しない
      if (isEdited) return null; // すでに編集ずみの場合は、デフォルト値を使用しない
      if (!isEditing) return null; // 編集中でない場合は、デフォルト値を使用しない

      const defaultValue = calcDefaultValue(this, enterpriseFormVersionItem);
      return defaultValue;
    },

    // 編集表示用データ
    formDataForEdit() {
      const {
        formDataForDisplay,
        reportFormVersionItemValueEdit: initialValue,
        enterpriseFormVersionItem,
        defaultValue,
      } = this;
      return {
        ...formDataForDisplay,
        initialValue,
        enterpriseFormVersionItem,
        defaultValue,
      };
    },

    reportFormVersionItemValueRead() {
      const { reportValue: formValue, enterpriseFormVersionItem } = this;
      // 既存 type は applyFormat 相当、新 type（value_json / FLEXIBLE_TIME_PICKER）は可読文字列へ整形
      return formatReportValueForDisplay(
        enterpriseFormVersionItem,
        formValue,
        this.masterMaps
      );
    },

    reportFormVersionItemValueEdit() {
      const { reportValue: formValue, effectiveValueForEdit } = this;
      return effectiveValueForEdit !== undefined
        ? effectiveValueForEdit
        : formValue; // 編集時は LATEST 置き換え時は直前レポートの値
    },

    // 編集時・編集権限ありで default_value === 'LATEST' かつ保存値が LATEST/空 のとき、直前レポートの値
    valueFromLatestReport() {
      const { enterpriseFormVersionItem } = this;
      if (!enterpriseFormService.hasDefaultValue(enterpriseFormVersionItem))
        return null;
      const dv = enterpriseFormService.getDefaultValue(
        enterpriseFormVersionItem
      );
      if (!isString(dv) || dv.toUpperCase() !== "LATEST") return null;
      return getLatestReportValueForFormItem(this, enterpriseFormVersionItem);
    },

    // 保存値が文字列 "LATEST" か（大文字小文字無視）
    isStoredValueLATEST() {
      const v = this.reportValue;
      return isString(v) && v.toUpperCase() === "LATEST";
    },

    // 編集時かつ編集権限がある項目で、LATEST を直前レポートの値で置き換えるか
    shouldReplaceLATESTWithLatest() {
      const {
        isEditing,
        updatable,
        valueFromLatestReport,
        reportValue,
        isStoredValueLATEST,
      } = this;
      if (!isEditing || !updatable) return false;
      if (valueFromLatestReport == null) return false;
      if (isStoredValueLATEST) return true;
      if (reportValue == null || reportValue === "") return true;
      return false;
    },

    // 編集表示用の実効値（LATEST の場合は直前レポートの値）
    effectiveValueForEdit() {
      const { shouldReplaceLATESTWithLatest, valueFromLatestReport } = this;
      if (shouldReplaceLATESTWithLatest && valueFromLatestReport != null) {
        return valueFromLatestReport;
      }
      return undefined;
    },

    hasReportFormVersionItemValue() {
      const { reportFormVersionItem } = this;
      return operationReportService.hasReportFormVersionItemValue(
        reportFormVersionItem
      );
    },

    unit() {
      return enterpriseFormService.getUnit(this.enterpriseFormVersionItem);
    },

    selectOptions() {
      return enterpriseFormService.getSelectOptions(
        this.enterpriseFormVersionItem
      ); //
    },

    isNullableChecked() {
      return operationReportService.isNullableChecked(
        this.reportFormVersionItem
      ); // boolean
    },

    nullableLabel() {
      return enterpriseFormService.getNullableLabelFromEnterpriseFormVersionItem(
        this.enterpriseFormVersionItem
      ); // string
    },

    isEdited() {
      return !isEmpty(this.reportFormVersionItem); // レポートのフォームの値がない時は、これまで編集していない
    },
    reportValue() {
      const { reportFormVersionItem } = this;
      return operationReportService.getValueFromReportFormVersionItem(
        reportFormVersionItem
      );
    },
  },

  methods: {
    ...mapActions({ setFormVersionItemValue: "setFormVersionItemValue" }),

    /** 編集時・LATEST を直前レポートの値で置き換えている場合、store に同期して送信時に正しい値が送られるようにする */
    syncReplacedLATESTToStore() {
      const {
        shouldReplaceLATESTWithLatest,
        valueFromLatestReport,
        enterpriseFormVersionItem,
        reportFormVersionItem,
        reportFormVersionId,
      } = this;
      if (
        !shouldReplaceLATESTWithLatest ||
        valueFromLatestReport == null ||
        !reportFormVersionId
      )
        return;
      const payload = calcNextFormItemValue(
        enterpriseFormVersionItem,
        reportFormVersionItem,
        {
          value: valueFromLatestReport,
          nullableChecked:
            operationReportService.isNullableChecked(reportFormVersionItem) ||
            false,
        }
      );
      if (this.repeatIndex != null) {
        payload.repeat_index = this.repeatIndex;
      }
      this.setFormVersionItemValue({ reportFormVersionId, ...payload });
    },

    onChange(
      value // { value: number | string | date | [{ "name": string; }], nullableChecked }
    ) {
      const {
        reportFormVersionId,
        enterpriseFormVersionItem,
        reportFormVersionItem,
      } = this;
      const nextFormItemValue = calcNextFormItemValue(
        enterpriseFormVersionItem,
        reportFormVersionItem,
        value
      );
      // 可変行グループのセルは repeat_index を付与して該当行に書き込む
      if (this.repeatIndex != null) {
        nextFormItemValue.repeat_index = this.repeatIndex;
      }
      const payload = {
        reportFormVersionId,
        ...nextFormItemValue,
      };

      this.setFormVersionItemValue(payload);
    },
  },

  watch: {
    // 編集時・LATEST 置き換え時は store に同期し、送信時に直前レポートの値が送られるようにする
    shouldReplaceLATESTWithLatest: {
      handler(shouldReplace) {
        if (shouldReplace) this.syncReplacedLATESTToStore();
      },
      immediate: true,
    },
  },
};
</script>
